import { useLayoutEffect, type CSSProperties } from "react";
import { axioTokens } from "../theme";

export const AUTH_VIEWPORT_HEIGHT = axioTokens.dimension.viewportHeight;
export const AUTH_VIEWPORT_HEIGHT_VAR = "var(--auth-viewport-height)";
export const AUTH_DOCUMENT_BACKDROP = axioTokens.brandPanelDeep;
export const AUTH_COLUMN_ALIGNMENT = {
  top: "flex-start",
  center: "center",
} as const;
export const AUTH_CENTERED_GRID_ROWS = axioTokens.dimension.authCenteredRows;

/**
 * Shared split auth / onboarding layout — keep login + join visually aligned.
 * Column tracks live in the `.authGrid` class so they can collapse to a single
 * column at the same breakpoint where the hero rail is hidden.
 */
export const AUTH_GRID: CSSProperties & {
  "--auth-viewport-height": string;
  "--auth-hero-background": string;
} = {
  "--auth-viewport-height": AUTH_VIEWPORT_HEIGHT,
  "--auth-hero-background": axioTokens.authHeroGradient,
  display: "grid",
  minHeight: AUTH_VIEWPORT_HEIGHT_VAR,
  background: AUTH_DOCUMENT_BACKDROP,
};

export function useAuthDocumentBackdrop(backdrop: string = AUTH_DOCUMENT_BACKDROP) {
  useLayoutEffect(() => {
    const appRoot = document.getElementById("root");
    const targets = [document.documentElement, document.body, appRoot].filter(
      (target): target is HTMLElement => target instanceof HTMLElement,
    );
    const previousBackgrounds = targets.map((target) => target.style.background);

    targets.forEach((target) => {
      target.style.background = backdrop;
    });

    return () => {
      targets.forEach((target, index) => {
        target.style.background = previousBackgrounds[index];
      });
    };
  }, [backdrop]);
}

export const AUTH_HERO: CSSProperties = {
  /* Symmetric vertical padding so centered hero copy lines up with the form card. */
  padding: axioTokens.dimension.authHeroPadding,
  /* Fallback for non-animated contexts — aurora class overrides this */
  background: axioTokens.authHeroGradient,
};

export const AUTH_FORM_COLUMN: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: axioTokens.dimension.authFormPadding,
  /* Shared auth form surface; the CSS class may animate this background. */
  background: axioTokens.authFormGradient,
  minHeight: AUTH_VIEWPORT_HEIGHT_VAR,
};

export function withoutPadding(style: CSSProperties): CSSProperties {
  const next: CSSProperties = { ...style };
  delete next.padding;
  return next;
}

export const AUTH_FORM_WIDTH = axioTokens.unit[580];
export const AUTH_HERO_LOGO_HEIGHT = axioTokens.unit[72];
export const AUTH_MOBILE_LOGO_HEIGHT = axioTokens.unit[56];

export const AUTH_HERO_TAGLINE: CSSProperties = {
  fontFamily: axioTokens.fontHead,
  fontSize: axioTokens.fontSize.authTagline,
  fontWeight: axioTokens.fontWeight.semibold,
  color: axioTokens.surface,
  lineHeight: axioTokens.lineHeight.hero,
  letterSpacing: axioTokens.letterSpacing.tighter,
  margin: axioTokens.unit[0],
};

/* ── Premium design tokens ──────────────────────── */

/** Form card — border only, sharp square */
export const GLASS_CARD: CSSProperties = {
  background: axioTokens.surface,
  border: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
  boxShadow: axioTokens.shadow.none,
  borderRadius: axioTokens.radius.md,
  padding: `${axioTokens.unit[48]}px ${axioTokens.unit[40]}px`,
};

/** Section for onboarding sub-panels */
export const GLASS_SECTION: CSSProperties = {
  background: axioTokens.authInput,
  border: `${axioTokens.unit[1]}px solid ${axioTokens.borderSubtle}`,
  borderRadius: axioTokens.radius.md,
  padding: `${axioTokens.unit[22]}px ${axioTokens.unit[24]}px`,
};

/** Gradient CTA button style */
export const CTA_GRADIENT_VIOLET: CSSProperties = {
  background: axioTokens.ctaVioletGradient,
  border: "none",
  transition: `transform ${axioTokens.duration.medium} ${axioTokens.easing.standard}, box-shadow ${axioTokens.duration.medium} ${axioTokens.easing.standard}`,
};

export const CTA_GRADIENT_BLUE: CSSProperties = {
  background: axioTokens.ctaBlueGradient,
  border: "none",
  transition: `transform ${axioTokens.duration.medium} ${axioTokens.easing.standard}, box-shadow ${axioTokens.duration.medium} ${axioTokens.easing.standard}`,
};



/** Input focus glow style (applied via onFocus/onBlur or className) */
export const INPUT_FOCUS_GLOW = {
  borderColor: axioTokens.violetAlpha50,
  boxShadow: axioTokens.shadow.focus,
} as const;
